import { compareAsc } from "date-fns";

import { Capability, getCanViewPage, hasSectionCapability } from "../../shared/capabilities";

import {
  activitySelectors,
  createAppSelector,
  pageOutcomeSelectors,
  pageSelectors,
  selectCurrentSectionId,
  topicSelectors,
} from "./base";
import { selectAllAssessmentInfo } from "./computed";
import {
  selectAuthorizedPages,
  selectPageCategoryByPageId,
  selectPageSectionIdByPageId,
  selectPagesForCurrentSection,
} from "./page";
import { selectCurrentUserComputedRole } from "./permissions";
import { getSortedPages, sortActivities, sortPagesForTopic, sortTopicActivities } from "./sorting";
import { PageWithDueDateType } from "./types";

import { AssessmentStatus } from "api/api-data-types";
import { isDateWithinLastXDays } from "components/materials/page/helpers";
import { ActivityCategory, PageType, TopicType } from "components/server-types";
import { DEFAULT_ORDER_FN } from "mds/hooks/use-ordered";
import { compactMap } from "utils/collections";
import { isSectionPageCategory } from "utils/page-utils";
import { HomeRole } from "utils/urls";

// This is different from selectPagesForCurrentSection in that it also selects topics-related pages
export const selectAllPagesWithDueDates = createAppSelector(
  [selectAuthorizedPages],
  (pages) => pages.filter((page) => page.due_at) as PageWithDueDateType[],
);

export const selectAllRecentlyAssessedPages = createAppSelector(
  [
    (_state, daysAgo: number) => daysAgo,
    (state, _daysAgo: number, homeRole?: HomeRole | null) => selectAuthorizedPages(state, homeRole),
  ],
  (daysAgo, pages) =>
    pages
      .filter((page) => isDateWithinLastXDays(page.assessments_published_at, daysAgo))
      .sort((a, b) =>
        compareAsc(a.assessments_published_at ?? "", b.assessments_published_at ?? ""),
      ),
);

export const selectReadyForAssessmentPages = createAppSelector(
  [selectAuthorizedPages, selectAllAssessmentInfo],
  (pages, statuses) =>
    pages
      .filter((page) => statuses[page.id]?.status === AssessmentStatus.ASSESSABLE)
      .sort((a, b) => compareAsc(a.due_at ?? "", b.due_at ?? "")),
);

export const selectPagesForTopic = createAppSelector(
  [(state, topicId: string) => topicId, pageSelectors.selectAll, (state) => state.activities],
  (topicId, pages, activities) => {
    return pages.filter((page) => {
      if (!page.activity_id) return false;
      const activity = activities[page.activity_id];
      return activity && activity.topic_id === topicId;
    });
  },
);

export const selectPagesForOutcome = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    pageSelectors.selectAll,
    pageOutcomeSelectors.selectAll,
  ],
  (sectionOutcomeId, pages, pageOutcomes) => {
    return pages.filter((page) =>
      pageOutcomes.some(
        (po) => po.section_outcome_id === sectionOutcomeId && po.page_id === page.id,
      ),
    );
  },
);

export const selectChunkedPagesForOutcomeByTopic = createAppSelector(
  [
    (state, sectionOutcomeId: string) => selectPagesForOutcome(state, sectionOutcomeId),
    activitySelectors.selectAll,
    topicSelectors.selectAll,
  ],
  (pages, activities, topics) => {
    const byTopic = topics.map((topic) => {
      const topicPages = pages.filter((page) => {
        const activity = activities.find((act) => act.id === page.activity_id);
        return activity?.topic_id === topic.id;
      });
      return { ...topic, pages: topicPages };
    });
    return byTopic;
  },
);

/**
 * This selector is very similar to `topicSelectors.filterForCurrentSection`, but it filters out topics
 * that should not be visible to the current user.
 * For instructors, the result of this selector is identical to `topicSelectors.filterForCurrentSection`.
 * But for students, this selector will filter out topics that have no released pages.
 */
export const selectVisibleTopicsForCurrentSection = createAppSelector(
  [
    pageSelectors.selectAll,
    activitySelectors.selectEntities,
    topicSelectors.filterForCurrentSection,
    selectCurrentUserComputedRole,
  ],
  (pages: PageType[], activities, topicsForCurrentSection: TopicType[], computedRole) => {
    if (hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION)) {
      return topicsForCurrentSection;
    }

    const releasedPages = pages.filter((page) => page.released_at);

    return topicsForCurrentSection.filter((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicHasReleasedPages = releasedPages.some((page) => {
        if (!page.activity_id) return false;
        const activity = activities[page.activity_id];
        return activity && activity.topic_id === topic.id;
      });
      return topicHasReleasedPages;
    });
  },
);

export const selectVisibleTopicsWithPages = createAppSelector(
  [
    pageSelectors.selectAll,
    activitySelectors.selectAll,
    topicSelectors.filterForCurrentSection,
    selectCurrentUserComputedRole,
  ],
  (pages, activities, topicsForCurrentSection, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    const topicsWithPages = topicsForCurrentSection.map((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicPages = sortPagesForTopic(topic.id, activities, pages);
      const released_page_count = topicPages.filter((page) => page.released_at).length;
      const unreleased_page_count = topicPages.filter((page) => !page.released_at).length;

      return {
        ...topic,
        pages: topicPages,
        released_page_count,
        unreleased_page_count,
      };
    });

    return canAuthorSection
      ? topicsWithPages
      : topicsWithPages.filter((t) => t.released_page_count > 0);
  },
);

export const selectSortedActivitiesByTopicId = createAppSelector(
  [(state, topicId: string) => topicId, activitySelectors.selectAll],
  (topicId, activities) =>
    activities.filter((activity) => activity.topic_id === topicId).sort(sortActivities),
);

export const selectPagesByTopicIdAndActivityCategory = createAppSelector(
  [
    (state, topicId: string) => topicId,
    (_state, _topicId: string, category: ActivityCategory) => category,
    pageSelectors.selectAll,
    activitySelectors.selectEntities,
    selectCurrentUserComputedRole,
  ],
  (topicId, category, pages, activities, computedRole) => {
    return pages.filter((page) => {
      if (!page.activity_id) return false;
      const activity = activities[page.activity_id];
      return (
        activity?.topic_id === topicId &&
        activity?.category === category &&
        getCanViewPage(page, computedRole)
      );
    });
  },
);

export const selectSortedActivitiesByTopicIdAndCategory = createAppSelector(
  [
    (state, topicId: string) => topicId,
    (_state, _topicId: string, category: ActivityCategory) => category,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (topicId, category, activities, pages, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    const activityIdsWithVisiblePages = new Set(
      compactMap(pages, (page) =>
        page.activity_id && getCanViewPage(page, computedRole) ? page.activity_id : null,
      ),
    );
    return activities
      .filter(
        (activity) =>
          activity.topic_id === topicId &&
          activity.category === category &&
          (canAuthorSection || activityIdsWithVisiblePages.has(activity.id)),
      )
      .sort(sortActivities);
  },
);

export const selectedSortedTopicActivitiesByPageId = createAppSelector(
  [
    (state, pageId: string) => pageId,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (pageId, activities, pages, computedRole) => {
    if (!pageId) return [];

    const foundPage = pages.find((page) => page.id === pageId);
    if (!foundPage) return [];

    const foundActivity = activities.find((activity) => activity.id === foundPage.activity_id);
    if (!foundActivity) return [];

    return sortTopicActivities(foundActivity.topic_id, activities, pages, computedRole);
  },
);

export const selectPageViewTopicId = createAppSelector(
  [
    (state, pageId?: string | null, topicIdParam?: string | null) => topicIdParam,
    selectedSortedTopicActivitiesByPageId,
    topicSelectors.filterForCurrentSection,
  ],
  (topicIdParam, topicActivities, topics): string | null => {
    const topicFromQuery = topics.find((topic) => topic.id === topicIdParam);
    return topicActivities[0]?.topic_id || topicFromQuery?.id || topics[0]?.id || null;
  },
);

export const selectPageViewPageIds = createAppSelector(
  [
    (state, pageId?: string | null) => pageId ?? null,
    selectPageSectionIdByPageId,
    selectPageCategoryByPageId,
    selectPageViewTopicId,
    (state, pageId?: string | null) => {
      const category = selectPageCategoryByPageId(state, pageId);
      return selectPagesForCurrentSection(state, category);
    },
    selectedSortedTopicActivitiesByPageId,
    selectCurrentSectionId,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (
    pageId,
    pageSectionId,
    pageCategory,
    topicId,
    sectionPages,
    topicActivities,
    currentSectionId,
    allActivities,
    allPages,
    computedRole,
  ) => {
    if (!pageId || !pageSectionId || (currentSectionId && pageSectionId !== currentSectionId)) {
      return getSortedPages(
        sortTopicActivities(topicId ?? "", allActivities, allPages, computedRole),
      ).map((page) => page.id);
    }

    if (pageCategory === "activity_page") {
      return getSortedPages(topicActivities).map((page) => page.id);
    }

    return sectionPages.toSorted(DEFAULT_ORDER_FN).map((page) => page.id);
  },
);

export const selectIsPageAccessible = createAppSelector(
  [
    (state, pageId?: string | null) => pageId ?? null,
    selectPageSectionIdByPageId,
    selectCurrentSectionId,
    selectPageViewPageIds,
  ],
  (pageId, pageSectionId, currentSectionId, pageIds) => {
    if (!pageId || !pageSectionId || (currentSectionId && pageSectionId !== currentSectionId)) {
      return false;
    }
    // Account for the case where a user is viewing a page that they have access to and then loses
    // their access to it, e.g., due to the page being unreleased or the user's role changing.
    return pageIds.includes(pageId);
  },
);

export const selectCurrentSectionHasMaterials = createAppSelector(
  [selectAuthorizedPages, topicSelectors.filterForCurrentSection],
  (pages, topics) => {
    if (topics.length > 0) {
      return true;
    }
    return pages.filter((p) => isSectionPageCategory(p.category)).length > 0;
  },
);
